import axios from '@/helpers/axios'
import alarmsApi from '@/api/alarms_api'
import dateUtil from '@/utils/date_util'

export default {
  namespaced: true,
  state: {
    /**
     * 因为 state 的内容是可变的，在 computed 中使用
     * computed () {
     *   loading () {
     *     // return this.$store.getters.loading // 需要在 getters.js 中定义
     *     return this.$store.state.app.loading
     *   }
     * }
     */
    loading: false, // 是否加载中
    isNetworkHealth: false, // 网络是否健康
    currentAdmin: {},
    currentServiceProvider: {},
    permittedAuthorizations: undefined, // 如果请求还没有返回，初始化一个 undefined，表明暂时还没有数据返回
    notice: {
      show: false,
      content: '',
      icon: '',
      timer: undefined
    },
    pointsAgainstCurrencyRatio: null, // 积分转化率
    // 吐司提示
    toast: {
      message: '',
      type: 'info',
      timer: undefined
    },
    preferredPageRecordsCount: 20,
    welcomeSpeech: '',
    meiqiaEnterpriseId: null,
    theme: null,
    settings: {},
    serviceUrls: {},
    preferredLanguage: null,
    preferredWelcomePage: null,
    // 报警信息
    unresolvedAlarms: [],
    // unreadAlarmCount: 0,
    unresolvedAlarmCount: 0,
    canQueryAlarms: true, //控制tooltip只有第一次进入,或者有数据更新时,才允许重新加载
    // 属于互联互通的运营商ID
    refreshTs: null, // 记录最近的刷新时间，用于通知全部需要响应的刷新
    isDebuggingBillingRule: false // 控制是否调试费率
  },
  /**
   * mutations 用于 actions 更新 state 的信息。
   * 通过 actions 调用 commit(MUTATION_NAME, MUTATION_PARAMS)
   */
  mutations: {
    // 统一更新入口
    updateState(state, { stateProp, stateValue }) {
      state[stateProp] = stateValue
    },
    toggleLoading(state, loading) {
      state.loading = loading
    },
    updateCurrentAdmin(state, currentAdmin) {
      state.currentAdmin = currentAdmin
    },
    updatePointsAgainstCurrencyRatio(state, pointsAgainstCurrencyRatio) {
      state.pointsAgainstCurrencyRatio = pointsAgainstCurrencyRatio
    },
    updateCurrentServiceProvider(state, currentServiceProvider) {
      state.currentServiceProvider = currentServiceProvider
    },
    updatePermittedAuthorization(state, permitted_authorizations) {
      state.permittedAuthorizations = permitted_authorizations
    },
    updateNotice(state, notice) {
      state.notice = notice
    },
    updateToast(state, { message, type, timer }) {
      state.toast = { message, type, timer }
    },
    updatePreferredPageRecordsCount(state, preferredPageRecordsCount) {
      state.preferredPageRecordsCount = preferredPageRecordsCount
    },
    updateWelcomeSpeech(state, welcomeSpeech) {
      state.welcomeSpeech = welcomeSpeech
    },
    updateMeiqiaEnterpriseId(state, meiqiaId) {
      state.meiqiaEnterpriseId = meiqiaId
    },
    updateTheme(state, theme) {
      state.theme = theme
    },
    updateAppName(state, appName) {
      state.appName = appName
    },
    updateSettings(state, settings) {
      state.settings = settings
    },
    updateServiceUrls(state, serviceUrls) {
      state.serviceUrls = serviceUrls
    },
    updatePreferredLanguage(state, language) {
      state.preferredLanguage = language
    },
    updatePreferredWelcomePage(state, language) {
      state.preferredWelcomePage = language
    },
    // 报警信息
    updateUnresolvedAlarms(state, unresolved_alarms) {
      state.unresolvedAlarms = unresolved_alarms
    },
    updateUnresolvedAlarmCount(state, unresolved_count) {
      state.unresolvedAlarmCount = unresolved_count
    },
    updateCanQueryAlarms(state, canQueryAlarms) {
      state.canQueryAlarms = canQueryAlarms
    },
    // 更新刷新时间
    updateRefreshTs(state, refreshTs) {
      state.refreshTs = refreshTs
    },
    // 调试费率
    updateIsDebuggingBillingRule(state, isDebuggingBillingRule) {
      state.isDebuggingBillingRule = isDebuggingBillingRule
    }
  },
  /**
   * action 通过 dispatch 调用，return 的内容是通过 promise 获取
   */
  actions: {
    /**
     * 切换加载中的状态
     *  更新状态：this.$store.dispatch('app/toggleLoading', { loading: true }).then(data=>{...})
     *
     * @param {*} context 上下文对象，dispatch 自带的参数，结构 { commit, dispatch, getters, state, ... }
     * @param {*} param 参数
     */
    toggleLoading({ commit }, { loading }) {
      commit('toggleLoading', loading)
    },
    // 更新网络状态
    toggleNetwork({ commit }, { health }) {
      commit('updateState', {
        stateProp: 'isNetworkHealth',
        stateValue: health
      })
    },
    async fetchInfoAfterSignIn({ commit }) {
      await axios.get('/backend/admins/console').then(response => {
        commit('updateCurrentAdmin', response.data.current_admin)
        commit('updateCurrentServiceProvider', response.data.current_service_provider)
        commit('updatePermittedAuthorization', response.data.permitted_authorizations)
        commit('updateUnresolvedAlarms', response.data.unresolved_alarms)
        commit('updateUnresolvedAlarmCount', response.data.unresolved_alarms_count)
        commit('updatePreferredPageRecordsCount', response.data.preferred_page_records_count)
        commit('updateWelcomeSpeech', response.data.welcome_speech)
        commit('updateMeiqiaEnterpriseId', response.data.meiqia_enterprise_id)
        commit('updateTheme', response.data.theme)
        commit('updateAppName', response.data.app_name)
        commit('updateSettings', response.data.settings)
        commit('updateServiceUrls', response.data.service_urls)
        commit('updatePreferredLanguage', response.data.preferred_language)
        commit('updatePreferredWelcomePage', response.data.preferred_welcome_page)
        commit('updatePointsAgainstCurrencyRatio', response.data.points_against_currency_ratio)
      })
    },
    updateNotice({ commit }, notice) {
      commit('updateNotice', notice)
    },
    updateToast({ commit }, { message, type, timer }) {
      commit('updateToast', { message, type, timer })
    },
    // 告警信息
    fetchAlarms({ commit }) {
      alarmsApi.getAlarms({ last_trigger_at: dateUtil.latestDaysParam(30), resolved: false }).then(resp => {
        commit('updateUnreadAlarms', resp.data.entities.slice(0, 5))
        commit('updateCanQueryAlarms', false)
      })
    },
    /**
     * 获取并更新未处理的报警数
     */
    fetchUnResolvedAlarmCount({ commit }) {
      alarmsApi.getSummary({ last_trigger_at: dateUtil.latestYearsParam(1) }).then(resp => {
        commit('updateUnresolvedAlarmCount', resp.data.unresolved_alarms_count)
      })
    },
    updateUnresolvedAlarmCount({ commit }, count) {
      commit('updateUnresolvedAlarmCount', count)
    },
    updateCanQueryAlarms({ commit }, can_query) {
      commit('updateCanQueryAlarms', can_query)
    },
    // 更新刷新的时间
    updateRefreshTs({ commit }) {
      commit('updateRefreshTs', moment())
    },
    // 更新刷新的时间
    updateIsDebuggingBillingRule({ commit }, isDebuggingBillingRule) {
      commit('updateIsDebuggingBillingRule', isDebuggingBillingRule)
    }
  }
}
